import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@components/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/card';
import { Badge } from '@ui/badge';
import { ListTodo, Clock, CheckCircle, Settings, Users, TrendingUp, Store } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { authService } from '@services/api';

export const Route = createFileRoute('/(private)/dashboard')({
  component: Dashboard,
});

function Dashboard() {
  const navigate = useNavigate();

  // Função para limpar a seleção atual e navegar para seleção de estabelecimento
  const handleTrocarEstabelecimento = () => {
    authService.clearEstablishmentSelection();
    navigate({ to: '/selecionar-estabelecimento' });
  };

  // Dados mockados para demonstração
  const stats = {
    totalHoje: 15,
    emFila: 3,
    preparando: 2,
    prontos: 1,
    tempoMedio: '8m',
    taxaConclusao: '93%'
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Fila Digital</h1>
        <p className="text-gray-600 mt-2">Visão geral do gerenciamento de pedidos em tempo real</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Hoje</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalHoje}</p>
                <p className="text-xs text-green-600">↑ 12% vs ontem</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-brand-primary-100 rounded-lg">
                <ListTodo className="h-6 w-6 text-brand-primary-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Em Fila</p>
                <p className="text-2xl font-bold text-brand-primary-600">{stats.emFila}</p>
                <p className="text-xs text-gray-500">Aguardando preparo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-warning-100 rounded-lg">
                <Clock className="h-6 w-6 text-warning-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tempo Médio</p>
                <p className="text-2xl font-bold text-warning-600">{stats.tempoMedio}</p>
                <p className="text-xs text-green-600">↓ 2m vs meta</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-success-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-success-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Taxa Conclusão</p>
                <p className="text-2xl font-bold text-success-600">{stats.taxaConclusao}</p>
                <p className="text-xs text-green-600">Excelente</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-brand-primary-200" onClick={() => navigate({ to: '/fila' })}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-brand-primary rounded-lg">
                  <ListTodo className="h-5 w-5 text-white" />
                </div>
                Gerenciar Fila
              </CardTitle>
              <Badge variant="ghost" className="text-brand-primary-600 bg-brand-primary-50">
                {stats.emFila + stats.preparando + stats.prontos} ativos
              </Badge>
            </div>
            <CardDescription>
              Gerenciar pedidos em tempo real com interface de 3 colunas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
              <span>• {stats.emFila} aguardando</span>
              <span>• {stats.preparando} preparando</span>
              <span>• {stats.prontos} prontos</span>
            </div>
            <Button className="w-full" variant="default">
              Ir para Fila
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="p-2 bg-warning rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              Relatórios
            </CardTitle>
            <CardDescription>
              Análise detalhada do desempenho e métricas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tempo médio hoje:</span>
                <span className="font-medium">{stats.tempoMedio}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Pedidos concluídos:</span>
                <span className="font-medium">{stats.totalHoje - stats.emFila - stats.preparando - stats.prontos}</span>
              </div>
            </div>
            <Button className="w-full" variant="outline">
              Ver Relatórios
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="p-2 bg-gray-600 rounded-lg">
                <Settings className="h-5 w-5 text-white" />
              </div>
              Configurações
            </CardTitle>
            <CardDescription>
              Ajustar parâmetros e configurações do sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tempo limite alerta:</span>
                <span className="font-medium">10 min</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Notificações:</span>
                <span className="font-medium">Ativas</span>
              </div>
            </div>
            <Button className="w-full" variant="outline">
              Configurar Sistema
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-gray-200" onClick={handleTrocarEstabelecimento}>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="p-2 bg-gray-500 rounded-lg">
                <Store className="h-5 w-5 text-white" />
              </div>
              Trocar Estabelecimento
            </CardTitle>
            <CardDescription>
              Alterar o estabelecimento ativo no sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Estabelecimento atual:</span>
                <span className="font-medium text-brand-primary-600">Padrão</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-success-600">Ativo</span>
              </div>
            </div>
            <Button className="w-full" variant="outline">
              Selecionar Outro Estabelecimento
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-gray-600" />
              Atividade Recente
            </CardTitle>
            <CardDescription>
              Últimas atualizações dos pedidos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center space-x-3">
                  <div className="h-2 w-2 bg-success rounded-full"></div>
                  <span className="text-sm">Pedido #105 concluído</span>
                </div>
                <span className="text-xs text-gray-500">há 2 min</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center space-x-3">
                  <div className="h-2 w-2 bg-warning rounded-full"></div>
                  <span className="text-sm">Pedido #104 em preparo</span>
                </div>
                <span className="text-xs text-gray-500">há 5 min</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className="h-2 w-2 bg-brand-primary rounded-full"></div>
                  <span className="text-sm">Novo pedido #102 recebido</span>
                </div>
                <span className="text-xs text-gray-500">há 12 min</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}